<script setup lang="ts" name="LoginScreen">
  import { UIAlert, UIButton, UIForm, UIFormItem, UIInput, UITextMdNormal, useNotification } from '@gohighlevel/ghl-ui'
  import { ref } from 'vue'
  import OTPScreen from '@/components/OTPScreen.vue'
  import { AuthService } from '@/service/AuthService'
  import { useStore } from '@/utils/store'
  import { getErrorMessage } from '@/utils/utils'

  import RecaptchaComponent from '@/components/RecaptchaComponent.vue'
  import { AltType } from '@/utils/const'
  import { AxiosError } from 'axios'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const otpSent = ref(false)
  const store = useStore()
  const formRef = ref()
  const formValue = ref({
    email: '',
  })
  const contactId = ref('')

  const submitLoading = ref(false)

  const notification = useNotification()

  const reCaptchaToken = ref<string | undefined>(undefined)
  const recaptchaError = ref('')
  const showRecaptcha = ref(false)

  const emit = defineEmits(['onLogin'])
  const rules = {
    email: {
      required: true,
      trigger: ['input', 'blur'],
      validator: (_: unknown, value: string) => {
        if (!value) {
          return new Error('Email is required')
        }
        if (!/\S+@\S+\.\S+/.test(value)) {
          return new Error('Please enter a valid email')
        }
        return true
      },
    },
  }

  const onVerify = (response?: string) => {
    if (!response) {
      recaptchaError.value = 'Verification failed! Please try again'

      return
    }
    recaptchaError.value = ''
    reCaptchaToken.value = response
  }

  const handleSubmit = async () => {
    submitLoading.value = true

    try {
      await formRef.value?.getForm().validate()
      const response = await AuthService.generateOtp({
        altId: store.locationId.value,
        email: formValue.value.email,
        altType: AltType.LOCATION,
        storeId: store.storeId.value,
        captchaToken: reCaptchaToken.value,
      })

      if (!response.data.status) {
        throw new Error(response.data.message)
      }
      showRecaptcha.value = false
      contactId.value = response.data?.data?.contactId
      otpSent.value = true
      recaptchaError.value = ''
    } catch (error: unknown) {
      console.error(error)
      if (error instanceof AxiosError && error.response?.status === 429) {
        recaptchaError.value = 'Please verify you are not a robot'
        showRecaptcha.value = true
      } else {
        notification.create({
          title: 'Error',
          description: getErrorMessage(error, 'Error sending OTP, please try again later'),
          type: 'error',
          duration: 5000,
        })
      }
    } finally {
      submitLoading.value = false
    }
  }
</script>

<template>
  <div v-if="!otpSent" class="max-w-96 mx-auto flex flex-col items-center">
    <h1 class="text-2xl font-semibold text-center">{{ store.storeName }}</h1>
    <UITextMdNormal class="mt-2 text-gray-500">{{ t('login.welcomeBack') }}</UITextMdNormal>
    <UIForm :model="formValue" ref="formRef" id="login-form" :rules="rules" class="w-full">
      <UIFormItem id="email-item" label="" path="email">
        <UIInput v-model="formValue.email" id="email" type="email" :placeholder="t('login.emailPlaceholder')" />
      </UIFormItem>
      <RecaptchaComponent v-if="showRecaptcha" @expired="() => (reCaptchaToken = undefined)" @verify="onVerify" />
      <div class="mt-2">
        <UIAlert v-if="recaptchaError" id="alert" type="error" :closable="false">
          <template #content>{{ recaptchaError }}</template>
        </UIAlert>
      </div>
      <UIButton :loading="submitLoading" @click="handleSubmit" class="w-full mt-4" id="login-submit-button" type="primary">
        {{ t('login.button') }}
      </UIButton>
    </UIForm>
  </div>
  <OTPScreen v-else :contactId="contactId" :email="formValue.email" @back-to-login="otpSent = false" @on-login="emit('onLogin')" />
</template>
